import type { MeetingTopic } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { archiveMeetingTopic, createMeetingTopic, unarchiveMeetingTopic, updateMeetingTopic } from "./api"

function useMeetingTopicsInvalidation() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: ["meeting-topics"] })
    queryClient.invalidateQueries({ queryKey: ["meetings"] })
    queryClient.invalidateQueries({ queryKey: ["meeting"] })
  }
}

export function useCreateMeetingTopic() {
  const getContext = useApiContext()
  const invalidate = useMeetingTopicsInvalidation()

  return useMutation({
    mutationFn: async (meetingTopic: Partial<MeetingTopic>) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createMeetingTopic(context, meetingTopic)
    },
    onSuccess: invalidate,
  })
}

export function useUpdateMeetingTopic() {
  const getContext = useApiContext()
  const invalidate = useMeetingTopicsInvalidation()

  return useMutation({
    mutationFn: async ({ id, meetingTopic }: { id: string; meetingTopic: Partial<MeetingTopic> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateMeetingTopic(context, id, meetingTopic)
    },
    onSuccess: invalidate,
  })
}

export function useArchiveMeetingTopic() {
  const getContext = useApiContext()
  const invalidate = useMeetingTopicsInvalidation()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return archiveMeetingTopic(context, id)
    },
    onSuccess: invalidate,
  })
}

export function useUnarchiveMeetingTopic() {
  const getContext = useApiContext()
  const invalidate = useMeetingTopicsInvalidation()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return unarchiveMeetingTopic(context, id)
    },
    onSuccess: invalidate,
  })
}
